import { useState, useMemo, useCallback } from "react";
import { useUserModule } from "../../contexts/UserModuleContext";
import { useUserContext } from "../../contexts/UserContext";
import { useEquipmentContext } from "../../contexts/EquipmentContext";
import { generateChangeLogEntry, groupTasksByCategory, buildNextEvaluationHistory, formatEvaluationDateDisplay, isSkillEvaluated } from "../../utils/userModuleUtils";
import { getSkillLevelColor } from "../../utils/skillLevelColors";
import { AccordionCard, Btn, Card, IconEdit, IconTrash, type AccordionCardItem } from "../ui";
import { EditTaskModal } from "./modals/EditTaskModal";
import type { AssignSkillInput, SkillCategory } from "../../types/userModuleTypes";
import "../../css/UserProfileTabs.css";
import "../../css/UserModuleModals.css";

interface SkillsetClassificationEvaluationsProps {
    userId: string;
    /** When true, evaluations are displayed without grade actions. Defaults to true. */
    readOnly?: boolean;
    onSetSkillLevel?: (taskId: string) => void;
    className?: string;
}

const formatDate = (dateStr: string | null | undefined): string =>
    formatEvaluationDateDisplay(dateStr);

/**
 * Classification evaluations for a single user — heading + assigned classification list.
 * Used on the user Skillsets tab and reusable from Grade User Skillsets.
 */
export const SkillsetClassificationEvaluations = ({
    userId,
    readOnly = true,
    onSetSkillLevel,
    className,
}: SkillsetClassificationEvaluationsProps) => {
    const {
        classifications,
        categories,
        tasks,
        subCategories,
        skillAssignments,
        skillLevels,
        assignSkill,
        updateSkillAssignment,
        deleteSkillAssignment,
        createChangeLogEntry,
    } = useUserModule();
    const { companyUser, companyOwner, allCompanyUsers } = useUserContext();
    const { companyUnits } = useEquipmentContext();

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editModalTaskId, setEditModalTaskId] = useState("");
    const [editModalTaskName, setEditModalTaskName] = useState("");
    const [editModalCategoryName, setEditModalCategoryName] = useState("");
    const [editModalClassificationName, setEditModalClassificationName] = useState("");
    const [filterSubCategoryByCategory, setFilterSubCategoryByCategory] = useState<
        Record<string, string | null>
    >({});
    const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<string | null>(null);

    const userAssignments = useMemo(
        () => skillAssignments.filter((a) => a.userId === userId),
        [skillAssignments, userId],
    );

    const tasksByCategory = useMemo(
        () => groupTasksByCategory(tasks, categories),
        [tasks, categories],
    );

    const assignedClassificationIds = useMemo(() => {
        const taskIds = new Set(userAssignments.map((a) => a.taskId));
        const categoryIds = new Set(
            tasks.filter((t) => taskIds.has(t.id)).map((t) => t.categoryId),
        );
        return new Set(
            categories.filter((c) => categoryIds.has(c.id)).map((c) => c.classificationId),
        );
    }, [userAssignments, tasks, categories]);

    const resolveAssignmentMachineName = (category: SkillCategory, subId: string): string => {
        if (category.equipmentCategoryId) {
            const unit = companyUnits.find((u) => u.id === subId);
            if (unit) return unit.name || subId;
        }
        const subCat = subCategories.find((sc) => sc.id === subId);
        if (subCat) return subCat.name;
        return subId;
    };

    const getSubCategoryPills = (category: SkillCategory): { id: string; name: string }[] => {
        const categoryTasks = tasks.filter((t) => t.categoryId === category.id);
        const categoryTaskIds = new Set(categoryTasks.map((t) => t.id));
        const assignedSubIds = new Set(
            userAssignments
                .filter((a) => categoryTaskIds.has(a.taskId) && a.subCategoryId)
                .map((a) => a.subCategoryId!),
        );
        if (assignedSubIds.size === 0) return [];

        const results: { id: string; name: string }[] = [];
        for (const subId of assignedSubIds) {
            results.push({ id: subId, name: resolveAssignmentMachineName(category, subId) });
        }
        return results;
    };

    const getLevelColor = (level: string | null) => getSkillLevelColor(level, skillLevels);
    const getSkillDotColor = (level: string | null): string => getLevelColor(level).dot;

    const handleSetSkillLevel = useCallback(
        (taskId: string) => {
            if (onSetSkillLevel) {
                onSetSkillLevel(taskId);
                return;
            }
            const task = tasks.find((t) => t.id === taskId);
            if (!task) return;
            const category = categories.find((c) => c.id === task.categoryId);
            const classification = category
                ? classifications.find((cl) => cl.id === category.classificationId)
                : null;
            setEditModalTaskId(taskId);
            setEditModalTaskName(task.name);
            setEditModalCategoryName(category?.name || "");
            setEditModalClassificationName(classification?.name || "");
            setEditModalVisible(true);
        },
        [onSetSkillLevel, tasks, categories, classifications],
    );

    const handleDeleteAssignment = useCallback(
        async (taskId: string) => {
            const assignmentsToDelete = userAssignments.filter((a) => a.taskId === taskId);
            if (assignmentsToDelete.length === 0) return;
            try {
                for (const assignment of assignmentsToDelete) {
                    await deleteSkillAssignment(assignment.id);
                }
            } catch (err) {
                console.error("Error deleting assignment:", err);
            }
        },
        [userAssignments, deleteSkillAssignment],
    );

    const handleSkillSave = useCallback(
        async (input: AssignSkillInput) => {
            if (!userId) return;
            const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || "";
            const changedBy = companyUser?.id || companyOwner?.id || "";
            const changedByName = companyUser?.name || companyOwner?.name || "Admin";
            const existingAssignment = userAssignments.find(
                (a) => a.taskId === input.taskId && a.subCategoryId === input.subCategoryId,
            );
            const oldLevel = existingAssignment?.skillLevel || null;

            try {
                if (existingAssignment) {
                    const task = tasks.find((t) => t.id === input.taskId);
                    const category = task ? categories.find((c) => c.id === task.categoryId) : null;
                    const machineName =
                        input.subCategoryId && category
                            ? resolveAssignmentMachineName(category, input.subCategoryId)
                            : null;
                    const evaluationHistory = buildNextEvaluationHistory(existingAssignment, machineName);
                    await updateSkillAssignment(existingAssignment.id, {
                        skillLevel: input.skillLevel,
                        evaluatedBy: input.evaluatedBy,
                        evaluatedByName: input.evaluatedByName,
                        evaluationDate: input.evaluationDate,
                        evaluationHistory,
                    });
                } else {
                    await assignSkill(input);
                }
                const taskName = editModalTaskName;
                const levelRecord = skillLevels.find((sl) => sl.name === input.skillLevel);
                const levelWithDesc = levelRecord?.description
                    ? `${input.skillLevel} — ${levelRecord.description}`
                    : input.skillLevel;
                const evalDateLabel = formatEvaluationDateDisplay(input.evaluationDate) || "—";
                const task = tasks.find((t) => t.id === input.taskId);
                const category = task ? categories.find((c) => c.id === task.categoryId) : null;
                const machineName =
                    input.subCategoryId && category
                        ? resolveAssignmentMachineName(category, input.subCategoryId)
                        : null;
                const machineClause = machineName ? ` on machine '${machineName}'` : "";
                const logInput = generateChangeLogEntry(
                    userId,
                    companyId,
                    "Skill Level",
                    oldLevel,
                    input.skillLevel,
                    changedBy,
                    changedByName,
                );
                logInput.description = `Skillset updated — '${taskName}'${machineClause} skill level set to '${levelWithDesc}' on ${evalDateLabel} by ${input.evaluatedByName}`;
                await createChangeLogEntry(logInput);
            } catch (err) {
                console.error("Error saving skill assignment:", err);
            }
        },
        [
            userId,
            companyUser,
            companyOwner,
            userAssignments,
            editModalTaskName,
            skillLevels,
            tasks,
            categories,
            companyUnits,
            subCategories,
            assignSkill,
            updateSkillAssignment,
            createChangeLogEntry,
        ],
    );

    const editModalExistingAssignment = useMemo(
        () => userAssignments.find((a) => a.taskId === editModalTaskId) || null,
        [userAssignments, editModalTaskId],
    );

    const assignedClassifications = useMemo(
        () => classifications.filter((c) => assignedClassificationIds.has(c.id)),
        [classifications, assignedClassificationIds],
    );

    const toggleMachineFilter = useCallback((categoryId: string, pillId: string) => {
        setFilterSubCategoryByCategory((prev) => ({
            ...prev,
            [categoryId]: prev[categoryId] === pillId ? null : pillId,
        }));
    }, []);

    const classificationAccordionItems = useMemo((): AccordionCardItem[] => {
        return assignedClassifications.map((classification) => {
            const classCategories = categories.filter((c) => c.classificationId === classification.id);
            const categoriesWithAssignments = classCategories.filter((category) => {
                const categoryTasks = tasksByCategory.get(category.id) || [];
                return categoryTasks.some((task) => userAssignments.some((a) => a.taskId === task.id));
            });

            return {
                id: classification.id,
                title: classification.name,
                meta: `Classification · ${categoriesWithAssignments.length} ${
                    categoriesWithAssignments.length === 1 ? "category" : "categories"
                }`,
                cardClasses: "upt-skillsets-classification-card",
                children: (
                    <div className="upt-skillsets-classification-body">
                        {categoriesWithAssignments.map((category) => {
                            const categoryTasks = (tasksByCategory.get(category.id) || []).filter((task) =>
                                userAssignments.some((a) => a.taskId === task.id),
                            );
                            const activeMachineFilter = filterSubCategoryByCategory[category.id] ?? null;
                            const visibleTasks = categoryTasks.filter((task) => {
                                if (!activeMachineFilter) return true;
                                return userAssignments.some(
                                    (a) => a.taskId === task.id && a.subCategoryId === activeMachineFilter,
                                );
                            });
                            const pills = getSubCategoryPills(category);

                            return (
                                <Card
                                    key={category.id}
                                    variant="card-sm"
                                    className="upt-skillsets-category-section"
                                >
                                    <div className="upt-skillsets-category-header-v2">
                                        <div className="upt-skillsets-category-left">
                                            <span className="upt-skillsets-category-name-v2">
                                                {category.name}
                                            </span>
                                            {pills.length > 0 && (
                                                <div
                                                    className="upt-skillsets-pills"
                                                    role="group"
                                                    aria-label={`${category.name} machine filters`}
                                                >
                                                    {activeMachineFilter && (
                                                        <button
                                                            type="button"
                                                            className="badge badge-neutral btn-sm upt-skillsets-show-all"
                                                            onClick={() =>
                                                                setFilterSubCategoryByCategory((prev) => ({
                                                                    ...prev,
                                                                    [category.id]: null,
                                                                }))
                                                            }
                                                        >
                                                            Show all
                                                        </button>
                                                    )}
                                                    {pills.map((p) => (
                                                        <button
                                                            key={p.id}
                                                            type="button"
                                                            className={`badge badge-neutral btn-sm${
                                                                activeMachineFilter === p.id ? " is-active" : ""
                                                            }`}
                                                            aria-pressed={activeMachineFilter === p.id}
                                                            onClick={() => toggleMachineFilter(category.id, p.id)}
                                                        >
                                                            {p.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="upt-skillsets-category-tasks">
                                        {visibleTasks.length === 0 ? (
                                            <p className="upt-skillsets-filter-empty">
                                                No tasks for this machine filter. Choose another machine or Show all.
                                            </p>
                                        ) : (
                                            visibleTasks.map((task) => {
                                                const taskAssigns = userAssignments.filter(
                                                    (a) => a.taskId === task.id,
                                                );
                                                const normalizedLevels = taskAssigns.map((a) =>
                                                    !a.skillLevel || a.skillLevel === "Not Evaluated"
                                                        ? "Not Evaluated"
                                                        : a.skillLevel,
                                                );
                                                const uniqueLevels = [...new Set(normalizedLevels)];
                                                const allSameLevel = uniqueLevels.length === 1;
                                                const overallLevel = allSameLevel ? uniqueLevels[0] : null;
                                                const isMixed = uniqueLevels.length > 1;
                                                const mostRecentEval = taskAssigns
                                                    .filter((a) => isSkillEvaluated(a) && a.evaluationDate)
                                                    .sort((a, b) =>
                                                        (b.evaluationDate || "").localeCompare(
                                                            a.evaluationDate || "",
                                                        ),
                                                    )[0];

                                                return (
                                                    <div
                                                        key={task.id}
                                                        className="upt-skillsets-task-row-v2 admin"
                                                    >
                                                        <div className="upt-skillsets-task-left">
                                                            <span className="upt-skillsets-task-name-v2">
                                                                {task.name}
                                                            </span>
                                                            {category.allowMachineNumber && (
                                                                <span className="upt-skillsets-task-meta">
                                                                    Machine # optional
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="upt-skillsets-task-center">
                                                            {isMixed ? (
                                                                <div
                                                                    className="upt-skillsets-mixed-breakdown"
                                                                    role="group"
                                                                    aria-label="Mixed skill levels by machine"
                                                                >
                                                                    <span className="upt-skillsets-level-badge-v2 mixed">
                                                                        <span
                                                                            className="upt-dot"
                                                                            style={{ background: "#8b5cf6" }}
                                                                        />
                                                                        Mixed
                                                                    </span>
                                                                    {taskAssigns
                                                                        .filter((a) => a.subCategoryId)
                                                                        .map((a) => {
                                                                            const level =
                                                                                !a.skillLevel ||
                                                                                a.skillLevel === "Not Evaluated"
                                                                                    ? "Not Evaluated"
                                                                                    : a.skillLevel;
                                                                            const machineName =
                                                                                resolveAssignmentMachineName(
                                                                                    category,
                                                                                    a.subCategoryId!,
                                                                                );
                                                                            const colors = getLevelColor(level);
                                                                            return (
                                                                                <span
                                                                                    key={a.id}
                                                                                    className="upt-skillsets-machine-level"
                                                                                    style={{
                                                                                        background: colors.bg,
                                                                                        color: colors.text,
                                                                                    }}
                                                                                    title={`${machineName}: ${level}`}
                                                                                >
                                                                                    <span className="upt-skillsets-machine-level-name">
                                                                                        {machineName}
                                                                                    </span>
                                                                                    <span className="upt-skillsets-machine-level-grade">
                                                                                        {level}
                                                                                    </span>
                                                                                </span>
                                                                            );
                                                                        })}
                                                                </div>
                                                            ) : overallLevel &&
                                                              overallLevel !== "Not Evaluated" ? (
                                                                <span
                                                                    className="upt-skillsets-level-badge-v2"
                                                                    style={{
                                                                        background:
                                                                            getLevelColor(overallLevel).bg,
                                                                        color: getLevelColor(overallLevel)
                                                                            .text,
                                                                    }}
                                                                >
                                                                    <span
                                                                        className="upt-dot"
                                                                        style={{
                                                                            background:
                                                                                getSkillDotColor(overallLevel),
                                                                        }}
                                                                    />
                                                                    {overallLevel}
                                                                </span>
                                                            ) : (
                                                                <span className="upt-skillsets-level-badge-v2 not-evaluated">
                                                                    <span
                                                                        className="upt-dot"
                                                                        style={{ background: "#94a3b8" }}
                                                                    />
                                                                    Not Evaluated
                                                                </span>
                                                            )}
                                                            {mostRecentEval && (
                                                                <span className="upt-skillsets-eval-info">
                                                                    Eval:{" "}
                                                                    {formatDate(mostRecentEval.evaluationDate)}{" "}
                                                                    · {mostRecentEval.evaluatedByName || "—"}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {!readOnly && (
                                                            <div className="upt-skillsets-task-actions-v2">
                                                                <Btn
                                                                    variant="secondary"
                                                                    buttonSize="sm"
                                                                    buttonIcon={<IconEdit />}
                                                                    ariaLabel="Grade proficiency"
                                                                    buttonOnClick={() =>
                                                                        handleSetSkillLevel(task.id)
                                                                    }
                                                                />
                                                                <Btn
                                                                    variant="danger-outlined"
                                                                    buttonSize="sm"
                                                                    buttonIcon={<IconTrash />}
                                                                    ariaLabel="Remove from user"
                                                                    buttonOnClick={() =>
                                                                        setDeleteConfirmTaskId(task.id)
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                ),
            };
        });
    }, [
        assignedClassifications,
        categories,
        tasksByCategory,
        userAssignments,
        filterSubCategoryByCategory,
        readOnly,
        toggleMachineFilter,
        handleSetSkillLevel,
        // getSubCategoryPills / color helpers are stable enough via closure deps above
        skillLevels,
        companyUnits,
        subCategories,
    ]);

    const rootClassName = ["skillset-classification-evaluations", className].filter(Boolean).join(" ");

    return (
        <div className={rootClassName}>
            <h3 className="upt-skillsets-evaluations-heading">
                Classification Evaluations{" "}
                <span className="upt-skillsets-evaluations-source">(from User Skillsets)</span>
            </h3>

            {assignedClassifications.length > 0 ? (
                <AccordionCard
                    className="upt-skillsets-list upt-skillsets-accordion"
                    expandMode="multiple"
                    defaultExpandedIds={assignedClassifications.map((c) => c.id)}
                    items={classificationAccordionItems}
                />
            ) : (
                <div className="upt-skillsets-list">
                    <div className="upt-empty-state">No classification evaluations assigned.</div>
                </div>
            )}

            {!readOnly && (
                <EditTaskModal
                    visible={editModalVisible}
                    taskId={editModalTaskId}
                    taskName={editModalTaskName}
                    categoryName={editModalCategoryName}
                    classificationName={editModalClassificationName}
                    userId={userId}
                    existingAssignment={editModalExistingAssignment}
                    onClose={() => setEditModalVisible(false)}
                    onSave={handleSkillSave}
                />
            )}

            {!readOnly && deleteConfirmTaskId && (
                <div className="umm-overlay" onClick={() => setDeleteConfirmTaskId(null)}>
                    <div className="umm-card umm-card-sm" onClick={(event) => event.stopPropagation()}>
                        <div className="umm-header">
                            <h2>Remove Task Assignment</h2>
                        </div>
                        <div className="umm-body">
                            <p>
                                This will remove{" "}
                                <strong>{tasks.find((t) => t.id === deleteConfirmTaskId)?.name || "this task"}</strong>{" "}
                                from{" "}
                                <strong>
                                    {allCompanyUsers.find((user) => user.id === userId)?.name?.trim() || "this user"}
                                </strong>
                                's skillset record.
                            </p>
                            <p>
                                The task itself will not be deleted — it remains in the skillset library and can be
                                re-assigned later.
                            </p>
                        </div>
                        <div className="umm-footer">
                            <Btn
                                variant="secondary-outlined"
                                buttonText="Cancel"
                                buttonOnClick={() => setDeleteConfirmTaskId(null)}
                            />
                            <Btn
                                variant="danger"
                                buttonText="Remove"
                                buttonOnClick={() => {
                                    void handleDeleteAssignment(deleteConfirmTaskId);
                                    setDeleteConfirmTaskId(null);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
